// NavBar.js
import React, { useRef, useState, useEffect } from 'react';
import { Layout, Menu, Select, Typography } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    PhoneOutlined,
    UserOutlined,
    LoginOutlined,
    LogoutOutlined,
    BankOutlined,
    CustomerServiceOutlined,
    SettingOutlined,
    BarChartOutlined,
    MergeOutlined
} from '@ant-design/icons';
import AccountSelectorModal from './widgets/AccountSelectorModal';
import { useSharedData } from './contexts/SharedDataContext';
import { useRBAC, RBAC_ROLES } from '../hooks/useRBAC';

const { Sider } = Layout;
const { Option } = Select;
const { Title } = Typography;

const NavBar = ({ onCompanyNamesChange }) => {
    const { isAuthenticated, loginWithRedirect, logout, getAccessTokenSilently } = useAuth0();
    const [companyNames, setCompanyNames] = useState([]);
    const navigate = useNavigate();
    const { selectedTenant, setSelectedTenant } = useSharedData();
    const [selectedKey, setSelectedKey] = useState('');

    // Use the RBAC hook to get user roles
    const { hasRole, roles } = useRBAC([]);

    // State to hold the authorized tenants and to control modal visibility
    const [authorizedTenants, setAuthorizedTenants] = useState([]);
    const [accountModalVisible, setAccountModalVisible] = useState(false);


    useEffect(() => {
        if (isAuthenticated) {
            fetchCompanyNames();
            // Fetch authorized tenants from the API
            const fetchTenants = async () => {
                try {
                    const accessToken = await getAccessTokenSilently();
                    const response = await axios.get('/api/authorized-tenants', {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'x-tenant': selectedTenant
                        },
                    });
                    const tenants = response.data.tenants || [];
                    setAuthorizedTenants(tenants);

                    // If there is more than one tenant and the current tenant is invalid, pop up the modal.
                    if (tenants.length > 1 && !tenants.includes(selectedTenant)) {
                        setAccountModalVisible(true);
                    }
                } catch (err) {
                    console.error('Error fetching authorized tenants in NavBar:', err);
                }
            };
            fetchTenants();
        }
    }, [isAuthenticated, getAccessTokenSilently, selectedTenant]);

    const fetchCompanyNames = async () => {
        try {
            const accessToken = await getAccessTokenSilently();
            const response = await axios.get('/api/company-names', {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'x-tenant': selectedTenant
                },
            });
            setCompanyNames(response.data);
        } catch (error) {
            console.error('Error fetching company names: ', error);
        }
    };

    const handleCompanyNamesChange = (selectedCompanyNames) => {
        onCompanyNamesChange(selectedCompanyNames);
    };

    const handleMenuClick = (key) => {
        console.log("Clicked on " + key);
        switch (key) {
            case '1':
                navigate('/agents');
                break;
            case '2':
                navigate('/calls');
                break;
            case '3':
                navigate('/live-calls');
                break;
            case '4':
                navigate('/test-configuration');
                break;
            case '5':
                navigate('/analytics');
                break;
            case '6':
                navigate('/smart-rules');
                break;
            default:
                break;
        }
    };

    // Helper function to check if user has any of the required roles
    const userHasRole = (requiredRoles) => {
        return roles.includes(RBAC_ROLES.DEV_ONLY) || requiredRoles.some(role => roles.includes(role));
    };

    // Define the navigation items with their required roles
    const navItems = [
        {
            key: '4',
            icon: <SettingOutlined />,
            label: 'Configuration',
            roles: RBAC_ROLES.SUPER_ADMIN,
            visible: userHasRole(RBAC_ROLES.SUPER_ADMIN)
        },
        {
            key: '5',
            icon: <BarChartOutlined />,
            label: 'Analytics',
            roles: RBAC_ROLES.ANY_STAFF,
            visible: userHasRole(RBAC_ROLES.ANY_STAFF)
        },
        {
            key: '6',
            icon: <MergeOutlined />,
            label: 'Smart Rules',
            roles: RBAC_ROLES.ANY_STAFF,
            visible: userHasRole(RBAC_ROLES.SUPER_ADMIN)
        },
        {
            key: '1',
            icon: <UserOutlined />,
            label: 'Agents',
            roles: RBAC_ROLES.ADMIN_OR_MANAGER,
            visible: userHasRole(RBAC_ROLES.DEV_ONLY)
        },
        {
            key: '2',
            icon: <PhoneOutlined />,
            label: 'Leads',
            roles: RBAC_ROLES.USER,
            visible: userHasRole(RBAC_ROLES.DEV_ONLY)
        },
        {
            key: '3',
            icon: <CustomerServiceOutlined />,
            label: 'Live Calls',
            roles: RBAC_ROLES.ADMIN,
            visible: userHasRole(RBAC_ROLES.DEV_ONLY)
        },

    ];

    // Filter out items that should not be visible
    const visibleNavItems = navItems.filter(item => item.visible);

    const initialLoadRef = useRef(false);

    useEffect(() => {
        if (!initialLoadRef.current && visibleNavItems.length > 0 && isAuthenticated) {
            initialLoadRef.current = true;
            const firstKey = visibleNavItems[0].key;
            setSelectedKey(firstKey);
            handleMenuClick(firstKey);
        }
    }, [visibleNavItems, isAuthenticated]);


    return (
        <Sider breakpoint="lg" collapsedWidth="0" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'auto' }}>
            <div className="logo-container">
                {/* Optionally add your logo here */}
            </div>
            <div style={{ flexGrow: 1, overflow: 'auto' }}>
                <Menu
                    mode="inline"
                    selectedKeys={[selectedKey]}
                    onClick={(e) => {
                        setSelectedKey(e.key);
                        handleMenuClick(e.key);
                    }}
                >
                    {visibleNavItems.map(item => (
                        <Menu.Item key={item.key} icon={item.icon}>
                            {item.label}
                        </Menu.Item>
                    ))}
                </Menu>
                <div className="company-selection-container">
                    <Title style={{ display: 'none', marginLeft: '16px', padding: '8px', color: '#207edc' }} level={5}>
                        <BankOutlined style={{ marginRight: '12px' }} />Properties
                    </Title>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select company names"
                        onChange={handleCompanyNamesChange}
                    >
                        {companyNames.map((companyName) => (
                            <Option key={companyName} value={companyName}>
                                {companyName}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>
            {/* Render the Account text link if the user has more than one account */}
            {authorizedTenants.length > 1 && (
                <div style={{ padding: '8px 16px', textAlign: 'center' }}>
                    <a onClick={() => setAccountModalVisible(true)} style={{ cursor: 'pointer' }}>
                        Account: {selectedTenant}
                    </a>
                </div>
            )}
            <div style={{ marginBottom: 60 }}>
                <Menu mode="inline" defaultSelectedKeys={['login']}>
                    {!isAuthenticated ? (
                        <Menu.Item key="login" icon={<LoginOutlined />} onClick={() => loginWithRedirect()}>
                            Login
                        </Menu.Item>
                    ) : (
                        <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={() => logout({ returnTo: window.location.origin })}>
                            Logout
                        </Menu.Item>
                    )}
                </Menu>
            </div>
            {/* Include the Account Selector Modal */}
            <AccountSelectorModal
                visible={accountModalVisible}
                onApply={(tenant) => {
                    setSelectedTenant(tenant);
                    localStorage.setItem('selectedTenant', tenant);
                    setAccountModalVisible(false);
                }}
                onCancel={() => setAccountModalVisible(false)}
            />
        </Sider>
    );
};

export default NavBar;
